import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRemovePracticeUser } from '../../../../hooks/usePracticeUsers';
import { useToast } from '../../../../providers/ToastProvider';
import type { PracticeUserRow } from '../../../../services/practiceUsers';
import { apiErrorMessage } from '../../../../utils/apiError';
import { RoleBadge } from '../../users/RoleBadge';
import { UserAvatar } from '../../users/UserAvatar';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

interface RemoveUserModalProps {
    practiceId: string;
    practiceName: string;
    user: PracticeUserRow;
    onClose: () => void;
}

// Unlinks the user from this practice - it never deletes their login or
// their access to any other practice, hence the reassuring sub-copy.
export function RemoveUserModal({ practiceId, practiceName, user, onClose }: RemoveUserModalProps) {
    const { showToast } = useToast();
    const removeUser = useRemovePracticeUser(practiceId);

    useEscapeKey(onClose, !removeUser.isPending);

    const handleConfirm = async () => {
        try {
            await removeUser.mutateAsync(user.email);
            showToast(`${user.fullname} removed from ${practiceName}`, 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to remove user'), 'error');
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-user-modal-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-8"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[460px] flex flex-col overflow-hidden">
                <div className="px-6 pt-5 pb-4 flex items-start gap-3.5">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-red-50 text-red-600">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2
                            id="remove-user-modal-title"
                            className="text-[17px] font-bold text-gray-900"
                        >
                            Remove {user.fullname} from {practiceName}?
                        </h2>
                        <p className="text-[13px] text-gray-600 leading-relaxed mt-1">
                            They keep their account and any other practices.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={removeUser.isPending}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 pb-5">
                    <div className="flex items-center gap-3 border border-gray-200 rounded-lg px-3.5 py-3">
                        <UserAvatar name={user.fullname} size={32} />
                        <div className="min-w-0 flex-1">
                            <span className="block text-[13px] font-semibold text-gray-900 truncate">
                                {user.fullname}
                            </span>
                            <span className="block text-[12px] text-gray-500 truncate">
                                {user.email}
                            </span>
                        </div>
                        <RoleBadge isAdmin={user.isAdmin} />
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2.5">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={removeUser.isPending}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={removeUser.isPending}
                        onClick={handleConfirm}
                        className="bg-red-600 text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-red-700 disabled:opacity-50"
                    >
                        {removeUser.isPending ? 'Removing…' : 'Remove'}
                    </button>
                </div>
            </div>
        </div>
    );
}
